import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * FlagTable renders a table of feature flags for a selected organization.
 * Users can toggle individual flags and select multiple flags for bulk
 * updates.  The component does not perform the API calls itself; it calls
 * the provided callback functions when the user initiates an action.
 */
function FlagTable({ flags, onToggle, onBulkUpdate }) {
  const [selected, setSelected] = useState(new Set());

  const handleSelect = (featureName, checked) => {
    const newSet = new Set(selected);
    if (checked) {
      newSet.add(featureName);
    } else {
      newSet.delete(featureName);
    }
    setSelected(newSet);
  };

  const toggleSelected = (enable) => {
    if (selected.size === 0) return;
    onBulkUpdate(Array.from(selected), enable);
    // Clear selections after update
    setSelected(new Set());
  };

  return (
    <div className="card mt-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <span>Feature Flags</span>
        <div>
          <button
            className="btn btn-sm btn-success me-2"
            disabled={selected.size === 0}
            onClick={() => toggleSelected(true)}
          >
            Enable Selected
          </button>
          <button
            className="btn btn-sm btn-danger"
            disabled={selected.size === 0}
            onClick={() => toggleSelected(false)}
          >
            Disable Selected
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        <table className="table table-striped mb-0">
          <thead>
            <tr>
              <th scope="col">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    const all = e.target.checked;
                    const newSet = new Set();
                    if (all) {
                      flags.forEach((f) => newSet.add(f.feature_name));
                    }
                    setSelected(newSet);
                  }}
                  checked={flags.length > 0 && selected.size === flags.length}
                />
              </th>
              <th scope="col">Feature</th>
              <th scope="col">Enabled</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {flags.map((flag) => (
              <tr key={flag.feature_name}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(flag.feature_name)}
                    onChange={(e) =>
                      handleSelect(flag.feature_name, e.target.checked)
                    }
                  />
                </td>
                <td>{flag.feature_name}</td>
                <td>{flag.enabled ? "Yes" : "No"}</td>
                <td>
                  <button
                    className={
                      "btn btn-sm " + (flag.enabled ? "btn-danger" : "btn-success")
                    }
                    onClick={() => onToggle(flag.feature_name, !flag.enabled)}
                  >
                    {flag.enabled ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
            {flags.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-3">
                  No feature flags found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

FlagTable.propTypes = {
  flags: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onBulkUpdate: PropTypes.func.isRequired,
};

export default FlagTable;
